/**
 * 💼 Gestión de Adicionales y Referentes para CMO
 *
 * Permite:
 * - Gestionar catálogo de referentes
 * - Asignar adicionales (adicional, complemento, obra social) a empleados
 * - Asignar referentes a empleados
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  IconButton,
  Chip,
  Grid,
  Autocomplete,
  Switch,
  FormControlLabel,
  TableSortLabel,
  InputAdornment
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Save,
  Cancel,
  SupervisorAccount,
  AttachMoney,
  Search
} from '@mui/icons-material';
import { API_BASE_URL } from '../../config';

const GestionAdicionalesReferentes = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Estado para referentes
  const [referentes, setReferentes] = useState([]);
  const [dialogReferenteOpen, setDialogReferenteOpen] = useState(false);
  const [referenteEdit, setReferenteEdit] = useState(null);
  const [formReferente, setFormReferente] = useState({
    nombre: '',
    cargo: '',
    email: '',
    telefono: '',
    activo: true,
    observaciones: ''
  });

  // Estado para adicionales de empleados
  const [empleados, setEmpleados] = useState([]);
  const [empleadoSeleccionado, setEmpleadoSeleccionado] = useState(null);
  const [dialogAdicionalesOpen, setDialogAdicionalesOpen] = useState(false);
  const [adicionalesEdit, setAdicionalesEdit] = useState(null);
  const [formAdicionales, setFormAdicionales] = useState({
    adicional: 0,
    complemento: 0,
    obra_social: 0,
    referente_id: '',
    fecha_desde: new Date().toISOString().split('T')[0],
    fecha_hasta: '',
    observaciones: ''
  });

  const [referentesActivos, setReferentesActivos] = useState([]);

  // Estado para búsqueda y ordenamiento
  const [searchQuery, setSearchQuery] = useState('');
  const [orderBy, setOrderBy] = useState('legajo');
  const [order, setOrder] = useState('asc');

  useEffect(() => {
    cargarReferentes();
    cargarEmpleadosConAdicionales();
    cargarReferentesActivos();
  }, []);

  const cargarReferentes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/adicionales-referentes/referentes`, {
        withCredentials: true
      });
      setReferentes(response.data);
    } catch (error) {
      console.error('Error al cargar referentes:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Error al cargar referentes'
      });
    } finally {
      setLoading(false);
    }
  };

  const cargarReferentesActivos = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/adicionales-referentes/referentes/activos`, {
        withCredentials: true
      });
      setReferentesActivos(response.data);
    } catch (error) {
      console.error('Error al cargar referentes activos:', error);
    }
  };

  const cargarEmpleadosConAdicionales = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/adicionales-referentes/empleados`, {
        withCredentials: true
      });
      setEmpleados(response.data);
    } catch (error) {
      console.error('Error al cargar empleados:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Error al cargar empleados'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialogReferente = (referente = null) => {
    if (referente) {
      setReferenteEdit(referente);
      setFormReferente({
        nombre: referente.nombre,
        cargo: referente.cargo || '',
        email: referente.email || '',
        telefono: referente.telefono || '',
        activo: referente.activo,
        observaciones: referente.observaciones || ''
      });
    } else {
      setReferenteEdit(null);
      setFormReferente({
        nombre: '',
        cargo: '',
        email: '',
        telefono: '',
        activo: true,
        observaciones: ''
      });
    }
    setDialogReferenteOpen(true);
  };

  const handleOpenDialogAdicionales = (empleado) => {
    setEmpleadoSeleccionado(empleado);

    if (empleado.adicionales_id) {
      // Editar existente
      setAdicionalesEdit({ id: empleado.adicionales_id });
      setFormAdicionales({
        adicional: empleado.adicional || 0,
        complemento: empleado.complemento || 0,
        obra_social: empleado.obra_social || 0,
        referente_id: empleado.referente_id || '',
        fecha_desde: empleado.fecha_desde ? empleado.fecha_desde.split('T')[0] : new Date().toISOString().split('T')[0],
        fecha_hasta: empleado.fecha_hasta ? empleado.fecha_hasta.split('T')[0] : '',
        observaciones: ''
      });
    } else {
      // Crear nuevo
      setAdicionalesEdit(null);
      setFormAdicionales({
        adicional: 0,
        complemento: 0,
        obra_social: 0,
        referente_id: '',
        fecha_desde: new Date().toISOString().split('T')[0],
        fecha_hasta: '',
        observaciones: ''
      });
    }

    setDialogAdicionalesOpen(true);
  };

  const handleGuardarReferente = async () => {
    try {
      setLoading(true);

      if (referenteEdit) {
        await axios.put(`${API_BASE_URL}/api/adicionales-referentes/referentes/${referenteEdit.id}`, formReferente, {
          withCredentials: true
        });
        setMessage({ type: 'success', text: 'Referente actualizado correctamente' });
      } else {
        await axios.post(`${API_BASE_URL}/api/adicionales-referentes/referentes`, formReferente, {
          withCredentials: true
        });
        setMessage({ type: 'success', text: 'Referente creado correctamente' });
      }

      setDialogReferenteOpen(false);
      cargarReferentes();
      cargarReferentesActivos();

    } catch (error) {
      console.error('Error al guardar referente:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Error al guardar referente'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGuardarAdicionales = async () => {
    try {
      setLoading(true);

      const datos = {
        legajo: empleadoSeleccionado.legajo,
        ...formAdicionales
      };

      if (adicionalesEdit) {
        await axios.put(`${API_BASE_URL}/api/adicionales-referentes/adicionales/${adicionalesEdit.id}`, formAdicionales, {
          withCredentials: true
        });
        setMessage({ type: 'success', text: 'Adicionales actualizados correctamente' });
      } else {
        await axios.post(`${API_BASE_URL}/api/adicionales-referentes/adicionales`, datos, {
          withCredentials: true
        });
        setMessage({ type: 'success', text: 'Adicionales creados correctamente' });
      }

      setDialogAdicionalesOpen(false);
      cargarEmpleadosConAdicionales();

    } catch (error) {
      console.error('Error al guardar adicionales:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Error al guardar adicionales'
      });
    } finally {
      setLoading(false);
    }
  };

  const formatearMoneda = (valor) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(valor || 0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const descendingComparator = (a, b, orderBy) => {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  };

  const filteredAndSortedEmpleados = empleados
    .filter(emp =>
      emp.legajo.toString().toLowerCase().includes(searchQuery.toLowerCase()) ||
      emp.nombre.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (emp.referente_nombre && emp.referente_nombre.toLowerCase().includes(searchQuery.toLowerCase()))
    )
    .sort(getComparator(order, orderBy));

  return (
    <Box>
      {message.text && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({ type: '', text: '' })}>
          {message.text}
        </Alert>
      )}

      <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
        <Tab label="Referentes" icon={<SupervisorAccount />} iconPosition="start" />
        <Tab label="Adicionales por Empleado" icon={<AttachMoney />} iconPosition="start" />
      </Tabs>

      {/* Tab 1: Referentes */}
      {tabValue === 0 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Catálogo de Referentes</Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleOpenDialogReferente()}
              >
                Nuevo Referente
              </Button>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Nombre</TableCell>
                      <TableCell>Cargo</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Teléfono</TableCell>
                      <TableCell align="center">Estado</TableCell>
                      <TableCell align="center">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {referentes.map((ref) => (
                      <TableRow key={ref.id}>
                        <TableCell><strong>{ref.nombre}</strong></TableCell>
                        <TableCell>{ref.cargo || '-'}</TableCell>
                        <TableCell>{ref.email || '-'}</TableCell>
                        <TableCell>{ref.telefono || '-'}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={ref.activo ? 'Activo' : 'Inactivo'}
                            color={ref.activo ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenDialogReferente(ref)}
                            title="Editar"
                          >
                            <Edit />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tab 2: Adicionales por Empleado */}
      {tabValue === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Configurar Adicionales por Empleado
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Haga clic en un empleado para configurar sus adicionales (adicional, complemento, obra social) y asignarle un referente.
            </Typography>

            {/* Campo de búsqueda */}
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar por legajo, nombre o referente..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'legajo'}
                          direction={orderBy === 'legajo' ? order : 'asc'}
                          onClick={() => handleRequestSort('legajo')}
                        >
                          Legajo
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'nombre'}
                          direction={orderBy === 'nombre' ? order : 'asc'}
                          onClick={() => handleRequestSort('nombre')}
                        >
                          Nombre
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right">
                        <TableSortLabel
                          active={orderBy === 'adicional'}
                          direction={orderBy === 'adicional' ? order : 'asc'}
                          onClick={() => handleRequestSort('adicional')}
                        >
                          Adicional
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right">
                        <TableSortLabel
                          active={orderBy === 'complemento'}
                          direction={orderBy === 'complemento' ? order : 'asc'}
                          onClick={() => handleRequestSort('complemento')}
                        >
                          Complemento
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right">
                        <TableSortLabel
                          active={orderBy === 'obra_social'}
                          direction={orderBy === 'obra_social' ? order : 'asc'}
                          onClick={() => handleRequestSort('obra_social')}
                        >
                          Obra Social
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'referente_nombre'}
                          direction={orderBy === 'referente_nombre' ? order : 'asc'}
                          onClick={() => handleRequestSort('referente_nombre')}
                        >
                          Referente
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="center">Acción</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredAndSortedEmpleados.map((emp) => (
                      <TableRow
                        key={emp.legajo}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleOpenDialogAdicionales(emp)}
                      >
                        <TableCell>{emp.legajo}</TableCell>
                        <TableCell>{emp.nombre}</TableCell>
                        <TableCell align="right">
                          {emp.adicional > 0 ? formatearMoneda(emp.adicional) : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {emp.complemento > 0 ? formatearMoneda(emp.complemento) : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {emp.obra_social > 0 ? formatearMoneda(emp.obra_social) : '-'}
                        </TableCell>
                        <TableCell>{emp.referente_nombre || '-'}</TableCell>
                        <TableCell align="center">
                          <IconButton size="small" color="primary">
                            <Edit />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Dialog para crear/editar referente */}
      <Dialog
        open={dialogReferenteOpen}
        onClose={() => setDialogReferenteOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {referenteEdit ? 'Editar Referente' : 'Nuevo Referente'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre *"
                value={formReferente.nombre}
                onChange={(e) => setFormReferente({ ...formReferente, nombre: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Cargo"
                value={formReferente.cargo}
                onChange={(e) => setFormReferente({ ...formReferente, cargo: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formReferente.email}
                onChange={(e) => setFormReferente({ ...formReferente, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Teléfono"
                value={formReferente.telefono}
                onChange={(e) => setFormReferente({ ...formReferente, telefono: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Observaciones"
                value={formReferente.observaciones}
                onChange={(e) => setFormReferente({ ...formReferente, observaciones: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formReferente.activo}
                    onChange={(e) => setFormReferente({ ...formReferente, activo: e.target.checked })}
                  />
                }
                label="Activo"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogReferenteOpen(false)} startIcon={<Cancel />}>
            Cancelar
          </Button>
          <Button
            onClick={handleGuardarReferente}
            variant="contained"
            startIcon={<Save />}
            disabled={!formReferente.nombre}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para configurar adicionales de empleado */}
      <Dialog
        open={dialogAdicionalesOpen}
        onClose={() => setDialogAdicionalesOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Configurar Adicionales - {empleadoSeleccionado?.nombre}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Adicional"
                value={formAdicionales.adicional}
                onChange={(e) => setFormAdicionales({ ...formAdicionales, adicional: parseFloat(e.target.value) || 0 })}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Complemento"
                value={formAdicionales.complemento}
                onChange={(e) => setFormAdicionales({ ...formAdicionales, complemento: parseFloat(e.target.value) || 0 })}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Obra Social"
                value={formAdicionales.obra_social}
                onChange={(e) => setFormAdicionales({ ...formAdicionales, obra_social: parseFloat(e.target.value) || 0 })}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                options={referentesActivos}
                getOptionLabel={(option) => `${option.nombre}${option.cargo ? ` - ${option.cargo}` : ''}`}
                value={referentesActivos.find(r => r.id === formAdicionales.referente_id) || null}
                onChange={(event, newValue) => {
                  setFormAdicionales({ ...formAdicionales, referente_id: newValue?.id || '' });
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Referente" />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="date"
                label="Fecha Desde *"
                value={formAdicionales.fecha_desde}
                onChange={(e) => setFormAdicionales({ ...formAdicionales, fecha_desde: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="date"
                label="Fecha Hasta"
                value={formAdicionales.fecha_hasta}
                onChange={(e) => setFormAdicionales({ ...formAdicionales, fecha_hasta: e.target.value })}
                InputLabelProps={{ shrink: true }}
                helperText="Vacío = indefinido"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Observaciones"
                value={formAdicionales.observaciones}
                onChange={(e) => setFormAdicionales({ ...formAdicionales, observaciones: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogAdicionalesOpen(false)} startIcon={<Cancel />}>
            Cancelar
          </Button>
          <Button
            onClick={handleGuardarAdicionales}
            variant="contained"
            startIcon={<Save />}
            disabled={!formAdicionales.fecha_desde}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GestionAdicionalesReferentes;
